// src/modules/app-views/account/pages/Subscription/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Row, Col } from 'antd';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import DocumentIdentifier from 'components/core/DocumentIdentifier';
import SubscriptionCard from './SubscriptionCard';
import ResourcesCard from './ResourcesCard';
import OperatorsCard from './OperatorsCard';
import EntityRegisterCard from './EntityRegisterCard';
import EntityChannelsCard from 'components/core/entity/EntityChannelsCard';
import AiBusinessContextCard from './AiBusinessContextCard';
import useZustand from 'hooks/useZustand';
import app from 'configs/appResolver';
import { openModal } from 'components/core/GenericModal';
import VoipGotoAuthModal from '../../modals/VoipGotoAuthModal';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OrganizationRecordTab = ({ setState, state = {}, ...restProps }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { data, loading, viewMode } = useDocumentContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const GOTO_RESOURCE_UID = 'moduleresources_voip_goto';

  const handleResourcesChange = async (newResources) => {
    const oldResources = state?.resources || [];
    const isAddingGoto = !oldResources.includes(GOTO_RESOURCE_UID)
                       && newResources.includes(GOTO_RESOURCE_UID);

    if (!isAddingGoto) {
      // Normal flow: any other resource toggle, or unchecking GoTo
      setState?.(prev => ({ ...prev, resources: newResources }));
      return;
    }

    // Adding VoIP GoTo: check if already connected
    try {
      const [statusData] = await axiosService.get({ url: API.GOTO.CONNECTION_STATUS, returnAsObject: true });
      if (statusData?.connected) {
        // Already connected - just check the resource, no modal
        setState?.(prev => ({ ...prev, resources: newResources }));
        return;
      }
    } catch {
      // Status check failed - proceed to modal anyway
    }

    // Not connected: open auth modal
    openModal(VoipGotoAuthModal, {
      onSuccess: () => {
        // OAuth succeeded - add resource to state
        setState?.(prev => ({ ...prev, resources: newResources }));
      },
      // If modal closes without onSuccess, resource is NOT added
    });
  };

  const handleOperatorsChange = operatorActiveIds => {
    setState?.(prev => ({ ...prev, operatorActiveIds }));
  };

  const handleOperatorsLoad = replicatedIds => {
    const isNewDocument = viewMode === 'add';

    setState?.(prev => ({
      ...prev,
      // New subscription: no pre-selection, user must choose each operator
      operatorActiveIds: isNewDocument ? [] : replicatedIds,
      operatorReplicatedIds: replicatedIds,
    }));
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    // parent boundary for area loading
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="personalData" isFetching={loading} data={data} />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <SubscriptionCard />
        </Col>
      </Row>

      {app.PRESET?.SHOW_RESOURCES_SUBSCRIPTION !== false && (
        <div className="mt-3">
          <ResourcesCard
            value={state?.resources || []}
            onChange={handleResourcesChange}
            disabled={viewMode === 'view' || app.PRESET?.CAN_CHANGE_RESOURCES === false}
            nativeResources={app.PRESET?.NATIVE_RESOURCES}
          />
        </div>
      )}

      {app.MODULES?.includes('appmultiOperators') && (
        <div className="mt-3">
          <OperatorsCard
            value={state?.operatorActiveIds || []}
            onChange={handleOperatorsChange}
            onLoad={handleOperatorsLoad}
            disabled={viewMode === 'view'}
            scrollLimit={false}
          />
        </div>
      )}

      <div className="mt-3">
        <EntityRegisterCard setState={setState} state={state} />
      </div>

      <div className="mt-3">
        <AiBusinessContextCard />
      </div>

      <EntityChannelsCard />

      {/* Document Identifier (_id and Legacy Code) */}
      {/*<div style={{ marginTop: 8 }}>*/}
      {/*  <DocumentIdentifier documentId={data?._id} legacyCode={data?.legacyCode} />*/}
      {/*</div>*/}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OrganizationRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
